import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getMe, logout } from '@/lib/api';
import { LogOut, Package, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { PageSEO } from '@/components/SEO';

export default function Account() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // First check localStorage for cached user
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      try {
        setUser(JSON.parse(cachedUser));
      } catch (e) {
        // Invalid cached data
      }
    }
    
    // Verify with API
    getMe()
      .then(res => {
        setUser(res.data);
        localStorage.setItem('user', JSON.stringify(res.data));
        setLoading(false);
      })
      .catch(() => {
        // Clear cached data and redirect to login
        localStorage.removeItem('user');
        localStorage.removeItem('session_token');
        navigate('/login');
      });
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('user');
      localStorage.removeItem('session_token');
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-900"></div>
      </div>
    );
  }
  
  if (!user) return null;

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="account-page">
      <PageSEO.account />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-start mb-12">
          <div>
            <h1 className="text-4xl md:text-5xl font-medium mb-4">My Account</h1>
            <p className="text-lg text-stone-600">{user.email}</p>
          </div>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/orders" className="group">
            <div className="border border-stone-200 rounded-lg p-8 hover:border-stone-900 transition-colors">
              <Package className="h-12 w-12 mb-4 text-stone-900" />
              <h2 className="text-2xl font-medium mb-2">My Orders</h2>
              <p className="text-stone-600">Track and view your order history</p>
            </div>
          </Link>

          {user.role === 'admin' && (
            <Link to="/admin" className="group">
              <div className="border border-stone-200 rounded-lg p-8 hover:border-stone-900 transition-colors">
                <UserIcon className="h-12 w-12 mb-4 text-stone-900" />
                <h2 className="text-2xl font-medium mb-2">Admin Panel</h2>
                <p className="text-stone-600">Manage products and orders</p>
              </div>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
